/**
 * Cookie utility functions with security attributes
 */

/**
 * Set a cookie with security attributes
 * @param {string} name - Cookie name
 * @param {string} value - Cookie value
 * @param {Object} options - Additional options
 * @param {number} options.days - Number of days until expiration
 * @param {boolean} options.secure - Whether to set Secure flag (default: true if HTTPS)
 * @param {string} options.sameSite - SameSite attribute (default: 'Lax')
 * @param {string} options.path - Path attribute (default: '/')
 */
export const setCookie = (name, value, options = {}) => {
  const { days = 365, secure = window.location.protocol === 'https:', sameSite = 'Lax', path = '/' } = options;

  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  const secureFlag = secure ? '; Secure' : '';
  const cookieString = `${name}=${encodeURIComponent(value)}${expires}; path=${path}; SameSite=${sameSite}${secureFlag}`;

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null if not found
 */
export const getCookie = (name) => {
  const nameEQ = name + '=';
  const ca = document.cookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return decodeURIComponent(c.substring(nameEQ.length, c.length));
  }
  return null;
};

/**
 * Delete a cookie by name
 * @param {string} name - Cookie name
 * @param {string} path - Path attribute (default: '/')
 */
export const deleteCookie = (name, path = '/') => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Lax`;
};

/**
 * Check if user has given cookie consent
 * @returns {boolean} - True if consent given
 */
export const hasCookieConsent = () => {
  const consent = getCookie('cookie_consent');
  return consent === 'accepted' || consent === 'essential_only';
};

/**
 * Check if user has accepted all cookies (analytics/marketing)
 * @returns {boolean} - True if all non‑essential cookies accepted
 */
export const hasAcceptedAllCookies = () => {
  return getCookie('cookie_consent') === 'accepted';
};

/**
 * Check if user has given explicit consent to sell anonymised cookie data
 * @returns {boolean}
 */
export const hasDataSaleConsent = () => {
  return getCookie('cookie_data_sale') === 'accepted';
};

/**
 * Set cookie consent choice
 * @param {string} choice - 'accepted', 'declined', or 'essential_only'
 */
export const setCookieConsent = (choice) => {
  setCookie('cookie_consent', choice, { days: 365 });
};

/**
 * Set user's preference about selling their cookie data
 * @param {string} choice - 'accepted' or 'declined'
 */
export const setDataSaleConsent = (choice) => {
  setCookie('cookie_data_sale', choice, { days: 365 });
};